import {Node} from "@xyflow/react";
import {
    CLUSTER_GAP,
    CLUSTER_MIN_NODES,
    HORIZONTAL_NODE_GAP
} from "./FamilyTreeLayoutConstants";
import {getNodeWidth} from "./FamilyTreeNodeSizing";
import {isOtherMembersSection} from "./FamilyTreeNodeUtils";

export type PartnerBlock = { nodes: Node[]; groupKey: string; width: number };

function applyBlockNodePositions(block: PartnerBlock, x: number) {
    if (block.nodes.length === 2) {
        const [left, right] = block.nodes;
        const leftWidth = getNodeWidth(left);
        const rightWidth = getNodeWidth(right);
        left.position.x = x + leftWidth / 2;
        right.position.x = x + leftWidth + HORIZONTAL_NODE_GAP + rightWidth / 2;
        return;
    }

    const node = block.nodes[0];
    node.position.x = x + getNodeWidth(node) / 2;
}

export function buildFamilyLayoutContext(
    levelMap: Map<number, Node[]>,
    partnerRightOf: Map<string, string>,
    parentMap: Map<string, string[]>
) {
    const partnerLeftOf = buildPartnerLeftOf(partnerRightOf);
    const totalFamilyNodes = Array.from(levelMap.values())
        .flat()
        .filter(node => !isOtherMembersSection(node)).length;
    const enableClusterGap = totalFamilyNodes >= CLUSTER_MIN_NODES;
    const childrenByParent = buildChildrenByParent(parentMap);
    const getParentsForNode = (node: Node) => {
        const parents = parentMap.get(node.id);
        return (parents && parents.length > 0)
            ? normalizeParentIdsToUnits(parents, partnerRightOf, partnerLeftOf)
            : [node.id];
    };
    const hasSharedParent = (left: Set<string>, right: Set<string>) =>
        Array.from(left).some(parentId => right.has(parentId));

    return {
        partnerLeftOf,
        enableClusterGap,
        childrenByParent,
        getParentsForNode,
        hasSharedParent
    };
}

export function buildPartnerLeftOf(partnerRightOf: Map<string, string>) {
    const partnerLeftOf = new Map<string, string>();
    partnerRightOf.forEach((rightId, leftId) => {
        partnerLeftOf.set(rightId, leftId);
    });
    return partnerLeftOf;
}

export function buildChildrenByParent(parentMap: Map<string, string[]>) {
    const childrenByParent = new Map<string, string[]>();
    parentMap.forEach((parents, childId) => {
        parents.forEach(parentId => {
            const existing = childrenByParent.get(parentId) ?? [];
            if (!existing.includes(childId)) {
                childrenByParent.set(parentId, [...existing, childId]);
            }
        });
    });
    return childrenByParent;
}

export function normalizeParentIdsToUnits(
    parentIds: string[],
    partnerRightOf: Map<string, string>,
    partnerLeftOf: Map<string, string>
) {
    const unitIds = new Set<string>();

    parentIds.forEach(parentId => {
        const partnerId = partnerRightOf.get(parentId) ?? partnerLeftOf.get(parentId);
        if (!partnerId) {
            unitIds.add(parentId);
            return;
        }

        unitIds.add([parentId, partnerId].sort((left, right) => left.localeCompare(right)).join(':'));
    });

    return Array.from(unitIds).sort((left, right) => left.localeCompare(right));
}

export function getChildrenKey(childrenByParent: Map<string, string[]>, id: string) {
    const children = childrenByParent.get(id);
    if (!children || children.length === 0) return undefined;
    return `children:${children.slice().sort((a, b) => a.localeCompare(b)).join(':')}`;
}

export function getGroupKeyFromMaps(
    node: Node,
    childrenByParent: Map<string, string[]>,
    parentMap: Map<string, string[]>,
    partnerRightOf?: Map<string, string>,
    partnerLeftOf?: Map<string, string>
) {
    const childrenKey = getChildrenKey(childrenByParent, node.id);
    if (childrenKey) return childrenKey;
    const parents = parentMap.get(node.id);
    if (!parents || parents.length === 0) return node.id;
    if (partnerRightOf && partnerLeftOf) {
        return normalizeParentIdsToUnits(parents, partnerRightOf, partnerLeftOf).join('|');
    }
    return parents.slice().sort((a, b) => a.localeCompare(b)).join(':');
}

export function getPairGroupKeyFromMaps(
    left: Node,
    right: Node,
    childrenByParent: Map<string, string[]>,
    parentMap: Map<string, string[]>,
    partnerRightOf?: Map<string, string>,
    partnerLeftOf?: Map<string, string>
) {
    const leftChildren = childrenByParent.get(left.id) ?? [];
    const rightChildren = childrenByParent.get(right.id) ?? [];
    if (leftChildren.length > 0 || rightChildren.length > 0) {
        const childIds = Array.from(new Set([...leftChildren, ...rightChildren]))
            .sort((a, b) => a.localeCompare(b));
        if (childIds.length > 0) return `children:${childIds.join(':')}`;
    }
    const leftParents = parentMap.get(left.id);
    const rightParents = parentMap.get(right.id);
    const leftHasParents = (leftParents?.length ?? 0) > 0;
    const rightHasParents = (rightParents?.length ?? 0) > 0;
    if (leftHasParents && !rightHasParents) return getGroupKeyFromMaps(left, childrenByParent, parentMap, partnerRightOf, partnerLeftOf);
    if (rightHasParents && !leftHasParents) return getGroupKeyFromMaps(right, childrenByParent, parentMap, partnerRightOf, partnerLeftOf);
    const leftKey = getGroupKeyFromMaps(left, childrenByParent, parentMap, partnerRightOf, partnerLeftOf);
    const rightKey = getGroupKeyFromMaps(right, childrenByParent, parentMap, partnerRightOf, partnerLeftOf);
    if (leftKey === rightKey) return leftKey;
    return [leftKey, rightKey].sort((a, b) => a.localeCompare(b)).join('|');
}

export function buildOrderedLevelNodes(
    levelNodes: Node[],
    partnerRightOf: Map<string, string>,
    partnerLeftOf: Map<string, string>
) {
    const nodeById = new Map(levelNodes.map(node => [node.id, node]));
    const ordered = levelNodes.slice().sort((a, b) => (a.position.x ?? 0) - (b.position.x ?? 0));
    const handled = new Set<string>();
    const newOrder: Node[] = [];

    for (const node of ordered) {
        if (handled.has(node.id)) continue;
        const rightId = partnerRightOf.get(node.id);
        if (rightId) {
            const rightNode = nodeById.get(rightId);
            if (rightNode) {
                newOrder.push(node, rightNode);
                handled.add(node.id);
                handled.add(rightId);
                continue;
            }
        }

        const leftId = partnerLeftOf.get(node.id);
        if (leftId && nodeById.has(leftId) && !handled.has(leftId)) {
            continue;
        }

        newOrder.push(node);
        handled.add(node.id);
    }

    ordered.forEach(node => {
        if (!handled.has(node.id)) {
            newOrder.push(node);
            handled.add(node.id);
        }
    });

    return newOrder;
}

export function getNodesCenter(nodes: Node[]) {
    if (nodes.length === 0) return undefined;
    const currentLeft = Math.min(...nodes.map(n => (n.position.x ?? 0) - getNodeWidth(n) / 2));
    const currentRight = Math.max(...nodes.map(n => (n.position.x ?? 0) + getNodeWidth(n) / 2));
    return (currentLeft + currentRight) / 2;
}

export function buildPartnerBlocks(
    ordered: Node[],
    partnerRightOf: Map<string, string>,
    getGroupKey: (node: Node) => string,
    getPairGroupKey: (left: Node, right: Node) => string
) {
    const blocks: PartnerBlock[] = [];
    let idx = 0;
    while (idx < ordered.length) {
        const node = ordered[idx];
        const rightId = partnerRightOf.get(node.id);
        const partner = rightId ? ordered[idx + 1] : undefined;
        if (partner && partner.id === rightId) {
            const width = getNodeWidth(node) + getNodeWidth(partner) + HORIZONTAL_NODE_GAP;
            blocks.push({
                nodes: [node, partner],
                groupKey: getPairGroupKey(node, partner),
                width
            });
            idx += 2;
            continue;
        }
        blocks.push({
            nodes: [node],
            groupKey: getGroupKey(node),
            width: getNodeWidth(node)
        });
        idx += 1;
    }
    return blocks;
}

export function enforcePartnerAdjacency(
    levelMap: Map<number, Node[]>,
    partnerRightOf: Map<string, string>,
    parentMap: Map<string, string[]>
) {
    const {
        partnerLeftOf,
        enableClusterGap,
        childrenByParent,
        getParentsForNode,
        hasSharedParent
    } = buildFamilyLayoutContext(levelMap, partnerRightOf, parentMap);

    const getGroupKey = (node: Node) => getGroupKeyFromMaps(node, childrenByParent, parentMap);
    const getPairGroupKey = (left: Node, right: Node) => getPairGroupKeyFromMaps(left, right, childrenByParent, parentMap);

    levelMap.forEach((nodesAtLevel, level) => {
        const levelNodes = nodesAtLevel.filter(node => !isOtherMembersSection(node));
        if (levelNodes.length < 2) return;
        if (!levelNodes.some(node => partnerRightOf.has(node.id) || partnerLeftOf.has(node.id))) return;

        const ordered = buildOrderedLevelNodes(levelNodes, partnerRightOf, partnerLeftOf);
        if (ordered.length === 0) return;

        const currentCenter = getNodesCenter(ordered);
        if (currentCenter === undefined) return;
        const enableClusterGapAtLevel = enableClusterGap && level >= 1;

        const blocks = buildPartnerBlocks(ordered, partnerRightOf, getGroupKey, getPairGroupKey);
        const blockParents = blocks.map(block => new Set(block.nodes.flatMap(node => getParentsForNode(node))));
        let clusterParents = new Set(blockParents[0]);
        const blockGaps = blocks.map((_, idx) => {
            if (idx === 0) return 0;
            const parents = blockParents[idx];
            const isSharedCluster = hasSharedParent(clusterParents, parents);
            if (isSharedCluster) {
                parents.forEach(parentId => clusterParents.add(parentId));
            } else {
                clusterParents = new Set(parents);
            }
            return enableClusterGapAtLevel && !isSharedCluster ? CLUSTER_GAP : HORIZONTAL_NODE_GAP;
        });

        const totalWidth = blocks.reduce((sum, block, idx) => sum + block.width + blockGaps[idx], 0);
        const startX = currentCenter - totalWidth / 2;
        let x = startX;
        for (let i = 0; i < blocks.length; i++) {
            x += blockGaps[i];
            const block = blocks[i];
            applyBlockNodePositions(block, x);
            x += block.width;
        }
    });
}

export function enforcePartnerMaxGap(
    levelMap: Map<number, Node[]>,
    partnerMap: Map<string, string>
) {
    if (partnerMap.size === 0) return;

    levelMap.forEach(nodesAtLevel => {
        const levelNodes = nodesAtLevel.filter(node => !isOtherMembersSection(node));
        if (levelNodes.length < 2) return;
        const nodeById = new Map(levelNodes.map(node => [node.id, node]));
        const handled = new Set<string>();

        levelNodes.forEach(node => {
            const partnerId = partnerMap.get(node.id);
            if (!partnerId || handled.has(node.id) || handled.has(partnerId)) return;
            const partnerNode = nodeById.get(partnerId);
            if (!partnerNode) return;

            handled.add(node.id);
            handled.add(partnerId);

            const left = (node.position.x ?? 0) <= (partnerNode.position.x ?? 0) ? node : partnerNode;
            const right = left === node ? partnerNode : node;

            const leftWidth = getNodeWidth(left);
            const rightWidth = getNodeWidth(right);
            const leftRight = (left.position.x ?? 0) + leftWidth / 2;
            const rightLeft = (right.position.x ?? 0) - rightWidth / 2;
            const gap = rightLeft - leftRight;
            if (gap <= HORIZONTAL_NODE_GAP + 0.5) return;

            const extra = gap - HORIZONTAL_NODE_GAP;
            const shiftLeft = extra / 2;
            const shiftRight = extra - shiftLeft;
            left.position.x = (left.position.x ?? 0) + shiftLeft;
            right.position.x = (right.position.x ?? 0) - shiftRight;
        });
    });
}
